import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './DashboardSelector.module.css';
import HeroTrain from './railIntro/HeroTrain';
import { isSmartiLabEnabled } from '../features/smartiLab/config';
import SmartiLabEntranceOverlay from '../features/smartiLab/components/SmartiLabEntranceOverlay';
import DashboardMessagingCenter from './DashboardMessagingCenter';
import ArtavaAttribution from './artava/ArtavaAttribution';
import ArtavaCTA from './artava/ArtavaCTA';
import {
  changeOwnPassword,
  fetchOwnProfile,
  requestAccountExtension,
  updateOwnProfile,
} from '../api/auth';
import { artavaBrand } from '../branding/artavaBrand';

const SMARTI_LAB_ROUTE = '/smarti-lab';

const emptyPasswordState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

function toProfileDraft(source = {}) {
  return {
    email: source.email || '',
    fullName: source.fullName || '',
    company: source.company || '',
    department: source.department || '',
    plant: source.plant || '',
    team: source.team || '',
    project: source.project || '',
    profileTitle: source.profileTitle || '',
    phone: source.phone || '',
    locale: source.locale || '',
    timezone: source.timezone || '',
    bio: source.bio || '',
    avatarDataUrl: source.avatarDataUrl || '',
  };
}

function toExtensionDraft(source = {}) {
  return {
    requestedPlan: source.licensePackage || 'evaluation',
    note: '',
  };
}

function formatDate(value, language) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(language || undefined);
}

function formatAccessState(state) {
  const normalized = String(state || '').trim().toLowerCase();
  if (normalized === 'trial') return 'Trial active';
  if (normalized === 'active') return 'Commercial active';
  if (normalized === 'expired') return 'Expired';
  if (normalized === 'pending_acceptance') return 'Waiting for acceptance';
  if (normalized === 'not_required') return 'No license gate';
  return normalized || 'Unknown';
}

function buildTrialNotice(user = {}, language) {
  if (String(user?.licenseState || '').trim().toLowerCase() !== 'trial') return '';
  const trialEndsAt = formatDate(user?.licenseTrialExpiresAt, language);
  return String(language || '').toLowerCase().startsWith('it')
    ? `La prova Smarti di 7 giorni resta attiva fino al ${trialEndsAt}. Dopo la prova, contatta ${artavaBrand.companyName} per attivare il piano commerciale.`
    : `Your 7-day Smarti trial is active until ${trialEndsAt}. After the trial, contact ${artavaBrand.companyName} to activate the commercial plan.`;
}

function buildAvatarLabel(user = {}) {
  const base = user.fullName || user.username || 'Smarti User';
  return base
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'SU';
}

export default function DashboardSelector({ onLogout, currentUser, onCurrentUserUpdated }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language?.startsWith('it');
  const labEnabled = isSmartiLabEnabled();
  const [showSmartiLabEntrance, setShowSmartiLabEntrance] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [extensionSaving, setExtensionSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [extensionError, setExtensionError] = useState('');
  const [extensionMessage, setExtensionMessage] = useState('');
  const [profilePayload, setProfilePayload] = useState(null);
  const [profileDraft, setProfileDraft] = useState(toProfileDraft(currentUser || {}));
  const [passwordDraft, setPasswordDraft] = useState(emptyPasswordState);
  const [extensionDraft, setExtensionDraft] = useState(toExtensionDraft(currentUser || {}));

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const result = await fetchOwnProfile();
      setProfilePayload(result);
      setProfileDraft(toProfileDraft(result.profile || result.user || {}));
      setExtensionDraft((current) => ({
        ...current,
        requestedPlan: result.user?.licensePackage || current.requestedPlan || 'evaluation',
      }));
    } catch (error) {
      setProfileError(error.message || 'Could not load your profile.');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSmartiLabClick = useCallback(() => {
    setShowSmartiLabEntrance((current) => (current ? current : true));
  }, []);

  const handleSmartiLabEntranceComplete = useCallback(() => {
    setShowSmartiLabEntrance(false);
    navigate(SMARTI_LAB_ROUTE);
  }, [navigate]);

  const effectiveUser = profilePayload?.user || currentUser || {};
  const avatarDataUrl = profileDraft.avatarDataUrl || effectiveUser.avatarDataUrl || '';
  const avatarInitials = buildAvatarLabel(effectiveUser);
  const extensionRequests = profilePayload?.extensionRequests || [];
  const planExpiresAt = effectiveUser.licenseExpiresAt || effectiveUser.licenseTrialExpiresAt || null;
  const trialNotice = buildTrialNotice(effectiveUser, i18n.language);
  const passwordReady =
    passwordDraft.currentPassword &&
    passwordDraft.newPassword &&
    passwordDraft.confirmPassword &&
    passwordDraft.newPassword === passwordDraft.confirmPassword;

  const handleProfileDraftChange = (key, value) => {
    setProfileDraft((current) => ({ ...current, [key]: value }));
  };

  const handlePasswordDraftChange = (key, value) => {
    setPasswordDraft((current) => ({ ...current, [key]: value }));
  };

  const handleExtensionDraftChange = (key, value) => {
    setExtensionDraft((current) => ({ ...current, [key]: value }));
  };

  const handleAvatarPicked = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setProfileError('Please choose a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 850 * 1024) {
      setProfileError('Profile image must be smaller than 850 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      handleProfileDraftChange('avatarDataUrl', String(reader.result || ''));
      setProfileError('');
    };
    reader.onerror = () => {
      setProfileError('Could not read the selected image.');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileError('');
    setProfileMessage('');
    try {
      const result = await updateOwnProfile(profileDraft);
      if (result.user) onCurrentUserUpdated?.(result.user);
      setProfileMessage(result.message || 'Profile updated successfully.');
      await loadProfile();
    } catch (error) {
      setProfileError(error.message || 'Could not update your profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    setPasswordError('');
    setPasswordMessage('');
    try {
      const result = await changeOwnPassword({
        currentPassword: passwordDraft.currentPassword,
        newPassword: passwordDraft.newPassword,
      });
      if (result.user) onCurrentUserUpdated?.(result.user);
      setPasswordMessage(result.message || 'Password updated successfully.');
      setPasswordDraft(emptyPasswordState);
      await loadProfile();
    } catch (error) {
      setPasswordError(error.message || 'Could not update your password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleExtensionRequest = async () => {
    setExtensionSaving(true);
    setExtensionError('');
    setExtensionMessage('');
    try {
      const result = await requestAccountExtension(extensionDraft);
      setExtensionMessage(result.message || 'Extension request sent successfully.');
      setExtensionDraft((current) => ({ ...current, note: '' }));
      await loadProfile();
    } catch (error) {
      setExtensionError(error.message || 'Could not send your extension request.');
    } finally {
      setExtensionSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.backgroundRail} aria-hidden="true">
        <HeroTrain className={styles.pageTrain} showTelemetry={false} />
      </div>

      <div className={styles.content}>
        <header className={styles.hero}>
          <div className={styles.heroMain}>
            <div className={styles.brandRow}>
              <img src="/smarti-mark.svg" alt="Smarti logo" className={styles.brandLogo} />
              <div>
                <p className={styles.eyebrow}>{t('Choose your workspace')}</p>
                <p className={styles.brandName}>Smarti</p>
              </div>
            </div>
            <h1 className={styles.title}>{t('Which dashboard do you want to open?')}</h1>
            
              
            <div className={styles.railCopy}>
              <p className={styles.railEyebrow}>Smarti Rail</p>
              <p className={styles.railTitle}>Brake Intelligence Command Center</p>
              <p className={styles.railDetail}>
                Real-time brake condition, failure prediction, and anomaly detection across the fleet.
              </p>
            </div>

            <a
              href={artavaBrand.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.brandSignature}
              aria-label={`${artavaBrand.companyName} website`}
            >
              <img
                src={artavaBrand.logoSrc}
                alt={artavaBrand.companyName}
                className={styles.brandSignatureLogo}
              />
              <div className={styles.brandSignatureCopy}>
                <span>{isItalian ? 'Sviluppato da' : 'Built by'}</span>
                <strong>{artavaBrand.companyName}</strong>
              </div>
            </a>
          </div>

          <aside className={styles.accountPanel}>
            <div className={styles.accountTop}>
              {avatarDataUrl ? (
                <img src={avatarDataUrl} alt={effectiveUser.fullName || effectiveUser.username || 'Profile'} className={styles.avatar} />
              ) : (
                <div className={styles.avatarFallback}>{avatarInitials}</div>
              )}
              <div className={styles.accountIdentity}>
                <span>{t('Signed in as')}</span>
                <strong>{effectiveUser.fullName || effectiveUser.username || 'Smarti user'}</strong>
                <p>{profileDraft.profileTitle || effectiveUser.profileTitle || effectiveUser.username || 'Normal user'}</p>
              </div>
            </div>

            <div className={styles.accountMetaGrid}>
              <div>
                <span>{t('Plan')}</span>
                <strong>{effectiveUser.licensePackage || 'evaluation'}</strong>
              </div>
              <div>
                <span>{t('Access')}</span>
                <strong>{formatAccessState(effectiveUser.licenseState)}</strong>
              </div>
              <div>
                <span>{t('Expires')}</span>
                <strong>{formatDate(planExpiresAt, i18n.language)}</strong>
              </div>
              <div>
                <span>{t('Company')}</span>
                <strong>{effectiveUser.company || 'Not set'}</strong>
              </div>
            </div>

            <div className={styles.accountActions}>
              <button type="button" onClick={() => setProfileOpen(true)} className={styles.profileButton}>
                {t('Open profile')}
              </button>
              <button type="button" onClick={onLogout} className={styles.logoutButton}>
                {t('Logout')}
              </button>
            </div>
            <ArtavaAttribution variant="compact" />
          </aside>
        </header>

        <section className={styles.cardGrid}>
          <button type="button" className={styles.dashboardCard} onClick={() => navigate('/production-dashboard')}>
            <span className={styles.cardBadge}>{t('Production')}</span>
            <strong>{t('Test Machines Dashboard')}</strong>
            <p>{t('Open the main production workspace with quality overview, Pareto, failure rate, root-problem entry points, and customizable machine-data widgets.')}</p>
            <em>{t('Best for uploaded test-machine CSV data and standard quality analysis.')}</em>
          </button>

          <button type="button" className={`${styles.dashboardCard} ${styles.burnInCard}`} onClick={() => navigate('/burn-in-dashboard')}>
            <span className={styles.cardBadge}>{t('Burn-in')}</span>
            <strong>{t('Burn-In Dashboard')}</strong>
            <p>{t('Open the separate burn-in workspace with burn-in status, failure concentration, upload health, timing trends, and customizable burn-in widgets.')}</p>
            <em>{t('Best for workbook-based burn-in imports stored in the dedicated burn-in database.')}</em>
          </button>

          <button type="button" className={`${styles.dashboardCard} ${styles.opsCard}`} onClick={() => navigate('/operations-center')}>
            <span className={styles.cardBadge}>{t('Reporting')}</span>
            <strong>{t('Make report')}</strong>
            <p>{t('Open the reporting workspace to choose charts, configure them with real filters, add comments, and export a polished PDF report.')}</p>
            <em>{t('Best for users who want to build and export customer-ready reports quickly.')}</em>
          </button>

          <DashboardMessagingCenter
            currentUser={effectiveUser}
            cardClassName={`${styles.dashboardCard} ${styles.messagingCard}`}
            badgeClassName={styles.cardBadge}
          />

          {currentUser?.role === 'admin' ? (
            <button type="button" className={`${styles.dashboardCard} ${styles.adminCard}`} onClick={() => navigate('/admin')}>
              <span className={styles.cardBadge}>{t('Administration')}</span>
              <strong>{t('Admin Panel')}</strong>
              <p>{t('Open the secure administration workspace for user access, online presence, IP controls, and full access-activity review.')}</p>
              <em>{t('Visible only for administrator accounts and backed by the durable auth activity store.')}</em>
            </button>
          ) : null}

          {labEnabled ? (
            <button type="button" className={`${styles.dashboardCard} ${styles.labCard}`} onClick={handleSmartiLabClick}>
              <span className={styles.cardBadge}>{t('Smarti Lab')}</span>
              <strong>{t('Smarti Lab')}</strong>
              <p>{t('Experimental analytics for unit history, station health, regressions, drift, and investigation workflows.')}</p>
              <em>
                <span className={styles.labPill}>{t('Test mode')}</span>
                {t('Best for testing deeper DB-driven intelligence without changing the existing dashboards.')}
              </em>
            </button>
          ) : null}
        </section>
      </div>

      {profileOpen ? (
        <div className={styles.profileOverlay} role="dialog" aria-modal="true">
          <div className={styles.profileModal}>
            <div className={styles.profileHeader}>
              <div>
                <p className={styles.profileEyebrow}>{t('Your Smarti profile')}</p>
                <h2 className={styles.profileTitle}>{t('Account, plan, and security')}</h2>
                <p className={styles.profileLead}>
                  {t('Keep your user identity current, check your plan and expiry, request an extension, and secure your account from one place.')}
                </p>
              </div>
              <button type="button" className={styles.closeButton} onClick={() => setProfileOpen(false)}>
                {t('Close')}
              </button>
            </div>

            {profileLoading ? <div className={styles.noticeCard}>{t('Loading your profile...')}</div> : null}
            {profileError ? <div className={`${styles.noticeCard} ${styles.errorNotice}`}>{profileError}</div> : null}
            {profileMessage ? <div className={`${styles.noticeCard} ${styles.successNotice}`}>{profileMessage}</div> : null}

            <div className={styles.profileOverviewGrid}>
              <article className={styles.miniCard}>
                <span>{t('Current plan')}</span>
                <strong>{effectiveUser.licensePackage || 'evaluation'}</strong>
                <p>{t('This package is the current commercial or evaluation label tied to your account.')}</p>
              </article>
              <article className={styles.miniCard}>
                <span>{t('Access state')}</span>
                <strong>{formatAccessState(effectiveUser.licenseState)}</strong>
                <p>{t('This reflects whether your account is in trial, fully active, expired, or waiting for license acceptance.')}</p>
              </article>
              <article className={styles.miniCard}>
                <span>{t('Expiry window')}</span>
                <strong>{formatDate(planExpiresAt, i18n.language)}</strong>
                <p>{t('Trial and commercial access dates are shown here so you can request an extension before access is interrupted.')}</p>
              </article>
              <article className={styles.miniCard}>
                <span>{t('Password changed')}</span>
                <strong>{formatDate(profilePayload?.profile?.lastPasswordChangedAt, i18n.language)}</strong>
                <p>{t('Use the security form below to rotate your password when needed.')}</p>
              </article>
            </div>

            <div className={styles.profileGrid}>
              <section className={styles.profileSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <p className={styles.sectionEyebrow}>{t('Identity')}</p>
                    <h3>{t('Profile card')}</h3>
                  </div>
                </div>

                <div className={styles.avatarEditor}>
                  {avatarDataUrl ? (
                    <img src={avatarDataUrl} alt={effectiveUser.fullName || effectiveUser.username || 'Profile'} className={styles.avatarLarge} />
                  ) : (
                    <div className={styles.avatarFallbackLarge}>{avatarInitials}</div>
                  )}
                  <div className={styles.avatarControls}>
                    <label className={styles.uploadButton}>
                      {t('Upload photo')}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleAvatarPicked} hidden />
                    </label>
                    <button type="button" className={styles.secondaryButton} onClick={() => handleProfileDraftChange('avatarDataUrl', '')}>
                      {t('Remove photo')}
                    </button>
                    <p>{t('Use a small square image. It will appear beside your name on this selector page.')}</p>
                  </div>
                </div>

                <div className={styles.formGrid}>
                  <label className={styles.field}>
                    <span>{t('Full name')}</span>
                    <input value={profileDraft.fullName} onChange={(event) => handleProfileDraftChange('fullName', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Email')}</span>
                    <input value={profileDraft.email} onChange={(event) => handleProfileDraftChange('email', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Profile title')}</span>
                    <input value={profileDraft.profileTitle} onChange={(event) => handleProfileDraftChange('profileTitle', event.target.value)} placeholder={t('Quality engineer, data analyst, project lead...')} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Phone')}</span>
                    <input value={profileDraft.phone} onChange={(event) => handleProfileDraftChange('phone', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Company')}</span>
                    <input value={profileDraft.company} onChange={(event) => handleProfileDraftChange('company', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Department')}</span>
                    <input value={profileDraft.department} onChange={(event) => handleProfileDraftChange('department', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Plant')}</span>
                    <input value={profileDraft.plant} onChange={(event) => handleProfileDraftChange('plant', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Team')}</span>
                    <input value={profileDraft.team} onChange={(event) => handleProfileDraftChange('team', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Project')}</span>
                    <input value={profileDraft.project} onChange={(event) => handleProfileDraftChange('project', event.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Locale')}</span>
                    <input value={profileDraft.locale} onChange={(event) => handleProfileDraftChange('locale', event.target.value)} placeholder="en-GB / it-IT" />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Timezone')}</span>
                    <input value={profileDraft.timezone} onChange={(event) => handleProfileDraftChange('timezone', event.target.value)} placeholder="Europe/Rome" />
                  </label>
                  <label className={`${styles.field} ${styles.fieldWide}`}>
                    <span>{t('Bio')}</span>
                    <textarea rows={4} value={profileDraft.bio} onChange={(event) => handleProfileDraftChange('bio', event.target.value)} placeholder={t('Add context about your role, factory, focus area, or how you use Smarti.')} />
                  </label>
                </div>

                <div className={styles.sectionActions}>
                  <button type="button" className={styles.primaryButton} onClick={handleProfileSave} disabled={profileSaving}>
                    {profileSaving ? t('Saving...') : t('Save profile')}
                  </button>
                </div>
              </section>

              <section className={styles.profileSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <p className={styles.sectionEyebrow}>{t('Account')}</p>
                    <h3>{t('Plan and access')}</h3>
                  </div>
                </div>

                {trialNotice ? (
                  <div className={`${styles.noticeCard} ${styles.successNotice}`}>
                    {trialNotice}
                  </div>
                ) : null}

                <div className={styles.accountFacts}>
                  <div><span>{t('Username')}</span><strong>{effectiveUser.username || '-'}</strong></div>
                  <div><span>{t('Request type')}</span><strong>{effectiveUser.requestType || 'demo'}</strong></div>
                  <div><span>{t('License package')}</span><strong>{effectiveUser.licensePackage || 'evaluation'}</strong></div>
                  <div><span>{t('Access state')}</span><strong>{formatAccessState(effectiveUser.licenseState)}</strong></div>
                  <div><span>{t('Trial ends')}</span><strong>{formatDate(effectiveUser.licenseTrialExpiresAt, i18n.language)}</strong></div>
                  <div><span>{t('Commercial expiry')}</span><strong>{formatDate(effectiveUser.licenseExpiresAt, i18n.language)}</strong></div>
                </div>

                <div className={styles.supportStrip}>
                  <strong>{isItalian ? 'Ti serve un rollout piu ampio o supporto software?' : 'Need a broader rollout or custom software support?'}</strong>
                  <p>{isItalian ? 'Artava puo attivare Smarti commercialmente oppure aprire una conversazione tecnica per dashboard, workflow e integrazioni.' : 'Artava can activate Smarti commercially or start a technical conversation for dashboards, workflows, and integrations.'}</p>
                </div>
                <ArtavaCTA
                  variant="inline"
                  title={isItalian ? 'Ti serve un nuovo software o un estensione Smarti?' : 'Need new software or a Smarti expansion?'}
                  body={isItalian ? 'Apri un contatto con Artava direttamente da Smarti e descrivi il contesto operativo del team.' : 'Open a conversation with Artava directly from Smarti and describe the operational context behind the request.'}
                />

                {extensionError ? <div className={`${styles.noticeCard} ${styles.errorNotice}`}>{extensionError}</div> : null}
                {extensionMessage ? <div className={`${styles.noticeCard} ${styles.successNotice}`}>{extensionMessage}</div> : null}

                <label className={styles.field}>
                  <span>{t('Requested plan')}</span>
                  <select value={extensionDraft.requestedPlan} onChange={(event) => handleExtensionDraftChange('requestedPlan', event.target.value)}>
                    <option value="evaluation">{t('evaluation')}</option>
                    <option value="pilot">{t('pilot')}</option>
                    <option value="production">{t('production')}</option>
                  </select>
                </label>
                <label className={styles.field}>
                  <span>{t('Why do you need the extension?')}</span>
                  <textarea
                    rows={4}
                    value={extensionDraft.note}
                    onChange={(event) => handleExtensionDraftChange('note', event.target.value)}
                    placeholder={t('Describe the project stage, why access should continue, expected duration, and whether you need pilot or production use.')}
                  />
                </label>
                <div className={styles.sectionActions}>
                  <button type="button" className={styles.primaryButton} onClick={handleExtensionRequest} disabled={extensionSaving}>
                    {extensionSaving ? t('Sending...') : t('Request extension')}
                  </button>
                </div>

                <div className={styles.historyPanel}>
                  <div className={styles.historyHeader}>
                    <strong>{t('Recent account-extension requests')}</strong>
                    <span>{t('Status history')}</span>
                  </div>
                  {extensionRequests.length ? extensionRequests.map((request) => (
                    <article key={request.id} className={styles.historyItem}>
                      <div className={styles.historyTop}>
                        <strong>{request.requestedPlan || effectiveUser.licensePackage || 'evaluation'}</strong>
                        <span className={styles.historyStatus}>{request.status || 'pending'}</span>
                      </div>
                      <p>{request.note || t('No note recorded.')}</p>
                      <div className={styles.historyMeta}>
                        <span>{formatDate(request.requestedAt, i18n.language)}</span>
                        <span>{request.emailSentAt ? t('Email sent') : request.emailDeliveryError ? t('Email failed') : t('Awaiting mail status')}</span>
                      </div>
                    </article>
                  )) : (
                    <p className={styles.emptyHistory}>{t('No extension requests yet. Use the form above when you need to prolong trial or commercial access.')}</p>
                  )}
                </div>
              </section>
            </div>

            <section className={styles.profileSection}>
              <div className={styles.sectionHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>{t('Security')}</p>
                  <h3>{t('Change password')}</h3>
                </div>
              </div>

              {passwordError ? <div className={`${styles.noticeCard} ${styles.errorNotice}`}>{passwordError}</div> : null}
              {passwordMessage ? <div className={`${styles.noticeCard} ${styles.successNotice}`}>{passwordMessage}</div> : null}

              <div className={styles.formGrid}>
                <label className={styles.field}>
                  <span>{t('Current password')}</span>
                  <input type="password" value={passwordDraft.currentPassword} onChange={(event) => handlePasswordDraftChange('currentPassword', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('New password')}</span>
                  <input type="password" value={passwordDraft.newPassword} onChange={(event) => handlePasswordDraftChange('newPassword', event.target.value)} placeholder={t('10+ chars, upper, lower, number, symbol')} />
                </label>
                <label className={styles.field}>
                  <span>{t('Confirm new password')}</span>
                  <input type="password" value={passwordDraft.confirmPassword} onChange={(event) => handlePasswordDraftChange('confirmPassword', event.target.value)} />
                </label>
                <div className={styles.passwordHint}>
                  <strong>{t('Security tip')}</strong>
                  <p>{t('Use a strong password that is unique to Smarti. Changing it here updates the account immediately and records the last password-change date.')}</p>
                </div>
              </div>

              <div className={styles.sectionActions}>
                <button type="button" className={styles.primaryButton} onClick={handlePasswordSave} disabled={passwordSaving || !passwordReady}>
                  {passwordSaving ? t('Updating...') : t('Update password')}
                </button>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      <SmartiLabEntranceOverlay open={showSmartiLabEntrance} onComplete={handleSmartiLabEntranceComplete} />
    </div>
  );
}
